import React, { useState, useCallback } from 'react';
import { useCampaignContext } from '@/features/campaigns/campaign-context';
import { faFileInvoice, faGaugeHigh, faList, faUser } from '@fortawesome/pro-light-svg-icons';
import {
  ExpandableSidebarSection,
  PageContentSection,
  PageSection
} from '../../components/layout/layout';
import { campaignOverviewTitle, PageTitle } from '@/features/shared/page-title';
import {
  CampaignDetailsRail,
  CampaignRoleAssignmentsRail
} from '@/components/display/details-rail';
import { ReviseExecutionButton } from '@/features/executions/revise-io-button';
import { useExecutionContext } from '@/features/executions/execution-context';
import { ExecutionTopSection } from '@/features/executions/execution-top-section';
import { ExecutionLineItemsTable } from '@/features/executions/execution-line-items-table';
import { CampaignTopNav } from '@/features/proposals/shared-campaign-nav';
import { trpc } from '@/utils/trpc-client';
import { isReady } from 'shared/src/utils/zod/trpc-zod-utils';
import { ExecutionLineItemWithMetricsTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { CampaignPerformanceRailV2 } from '@/features/shared/performance-rail';
import { MediaTeamLineItemFormContainer } from '@/features/line-items/line-item-form/media-trader-form/media-team-line-item-form-container';
import { ExecutionEditState } from '@/features/line-items/line-item-form/common/form-types';
import { CampaignIOsRail } from '@/features/insertion-orders/campaign-ios-rail';
import { IOWarningBanner } from '@/features/insertion-orders/io-warning-banner';
import { useCaptureCampaignView } from '@/utils/posthog/analytics';

export function ExecutionPage() {
  const { campaign } = useCampaignContext();
  useCaptureCampaignView();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const isIOsRailOpen = expandedSection === 'ios';
  const openIOsRail = useCallback(() => setExpandedSection('ios'), []);

  return (
    <div className="flex w-full flex-col">
      <PageTitle title={campaignOverviewTitle(campaign.campaign_name)} />
      <CampaignTopNav />
      <PageSection>
        <PageContentSection>
          <ExecutionMain isIOsRailOpen={isIOsRailOpen} openIOsRail={openIOsRail} />
        </PageContentSection>
        <Sidebar expandedSection={expandedSection} setExpandedSection={setExpandedSection} />
      </PageSection>
    </div>
  );
}

type SidebarProps = {
  expandedSection: string | null;
  setExpandedSection: (section: string | null) => void;
};

function Sidebar({ expandedSection, setExpandedSection }: SidebarProps) {
  const { execution } = useExecutionContext();
  const { ios, iosLoading, iosError } = useCampaignContext();
  const strategyMetrics = trpc.campaignMetrics.useQuery({ campaign_id: execution.campaign_id });

  const hasCompletedIO = ios.some(io => io.status === 'completed');
  const shouldShowIOsWarning = !iosLoading && !iosError && !hasCompletedIO;

  if (!isReady(strategyMetrics)) return <div>Loading...</div>;

  return (
    <ExpandableSidebarSection
      expandedSection={expandedSection}
      onExpandedSectionChange={setExpandedSection}
      sections={[
        {
          name: 'details',
          header: 'Zoho Details',
          icon: faList,
          render: () => <CampaignDetailsRail isStrategyDefined={true} strategy={execution} />
        },
        {
          name: 'users',
          header: 'Role Assignments',
          icon: faUser,
          render: () => <CampaignRoleAssignmentsRail />
        },
        {
          name: 'progress',
          header: 'Campaign Progress',
          icon: faGaugeHigh,
          render: () => (
            <CampaignPerformanceRailV2 strategy={execution} metrics={strategyMetrics.data} />
          )
        },
        {
          name: 'ios',
          header: 'Campaign IOs',
          icon: faFileInvoice,
          render: () => <CampaignIOsRail />,
          showNotificationDot: shouldShowIOsWarning
        }
      ]}
    />
  );
}

type ExecutionMainProps = {
  isIOsRailOpen: boolean;
  openIOsRail: () => void;
};

function ExecutionMain({ isIOsRailOpen, openIOsRail }: ExecutionMainProps) {
  const { lineItems } = useExecutionContext();
  const [formState, setFormState] = useState<ExecutionEditState>({ state: 'closed' });

  const closeForm = () => {
    setFormState({ state: 'closed' });
  };

  function setIsEditing(lineItemId: string) {
    const lineItem = lineItems.find(li => li.id === lineItemId);
    if (!lineItem) throw new Error('Line item not found');
    setFormState({ state: 'edit', lineItem });
  }

  // If the form is active, show it instead of the line items table
  switch (formState.state) {
    case 'edit':
      return <MediaTeamLineItemFormContainer onClose={closeForm} lineItem={formState.lineItem} />;
    case 'closed':
      return (
        <>
          <ExecutionTopSection />
          <IOWarningBanner isIOsRailOpen={isIOsRailOpen} openIOsRail={openIOsRail} />
          <LineItemsSection setIsEditing={setIsEditing} />
        </>
      );
  }
}

function LineItemsSection({ setIsEditing }: { setIsEditing: (lineItemId: string) => void }) {
  const { lineItems, execution } = useExecutionContext();
  const lineItemMetrics = trpc.lineItemsMetrics.useQuery({ strategy_id: execution.id });

  if (!isReady(lineItemMetrics)) return <div>Loading...</div>;

  const lineItemsWithMetrics = lineItems.map(lineItem => {
    const metrics = lineItemMetrics.data.data.find(m => m.line_item_id === lineItem.id);
    return { ...lineItem, ...metrics } as ExecutionLineItemWithMetricsTrpcResponse;
  });

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 py-2">
        <LineItemsCount length={lineItems.length} />
        <div className="flex flex-wrap items-center justify-end gap-2">
          <ReviseExecutionButton />
        </div>
      </div>
      <ExecutionLineItemsTable
        lineItems={lineItemsWithMetrics}
        execution={execution}
        setIsEditing={setIsEditing}
      />
    </div>
  );
}

function LineItemsCount(props: { length: number }) {
  return (
    <div className="flex items-center gap-4">
      <div className="text-lg font-bold">Line Items</div>
      <div className="rounded bg-blue-100 px-2 py-0.5 font-semibold text-gray-600">
        {props.length}
      </div>
    </div>
  );
}
